// src/hooks/useCurrency.js
import { useState, useEffect } from 'react';
import settingsService from 'services/settingsService';

export const useCurrency = () => {
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [currencyRates, setCurrencyRates] = useState({
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    RON: 4.56
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  const fetchCurrencySettings = async () => {
    try {
      setLoading(true);
      const [ratesResponse, currencyData] = await Promise.all([settingsService.getCurrencyRates(), settingsService.getDefaultCurrency()]);

      // Handle both old format (just rates) and new format (object with rates, last_updated, source)
      const rates = ratesResponse.rates || ratesResponse;
      setCurrencyRates(rates);
      setDefaultCurrency(currencyData.currency);
    } catch (error) {
      console.error('Failed to fetch currency settings:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Convert amount from one currency to another
   * Uses the currency_rates fetched from the backend
   * @param {number} amount - Amount to convert
   * @param {string} fromCurrency - Source currency code
   * @param {string} toCurrency - Target currency code (defaults to defaultCurrency)
   * @returns {number} Converted amount
   */
  const convertCurrency = (amount, fromCurrency, toCurrency = null) => {
    const targetCurrency = toCurrency || defaultCurrency;

    if (fromCurrency === targetCurrency) {
      return amount;
    }

    // Convert using cross-rate calculation: amount * (toRate / fromRate)
    // The rates are stored relative to a base currency (usually USD)
    // Example: Convert 100 USD to EUR with rates {USD:1.0, EUR:0.8589}
    // Result: 100 * (0.8589 / 1.0) = 85.89 EUR
    const fromRate = currencyRates[fromCurrency] || 1;
    const toRate = currencyRates[targetCurrency] || 1;

    const convertedAmount = amount * (toRate / fromRate);

    return convertedAmount;
  };

  /**
   * Convert an array of invoice objects and sum amounts in default currency
   * @param {Array} invoices - Array of invoice objects with 'amount' and 'currency' fields
   * @param {Function} filterFn - Optional filter function to apply before summing
   * @returns {number} Total amount in default currency
   */
  const sumInvoicesInDefaultCurrency = (invoices, filterFn = null) => {
    let filteredInvoices = invoices;

    if (filterFn) {
      filteredInvoices = invoices.filter(filterFn);
    }

    return filteredInvoices.reduce((sum, invoice) => {
      const amount = parseFloat(invoice.amount || 0);
      const convertedAmount = convertCurrency(amount, invoice.currency || 'USD');
      return sum + convertedAmount;
    }, 0);
  };

  /**
   * Calculate statistics with currency conversion
   * @param {Array} invoices - Array of invoice objects
   * @returns {Object} Statistics object with converted amounts
   */
  const calculateStatisticsWithConversion = (invoices) => {
    const stats = {
      total: invoices.length,
      draft: invoices.filter((inv) => inv.status === 'draft').length,
      submitted: invoices.filter((inv) => inv.status === 'submitted').length,
      approved: invoices.filter((inv) => inv.status === 'approved').length,
      rejected: invoices.filter((inv) => inv.status === 'rejected').length,
      paid: invoices.filter((inv) => inv.status === 'paid').length,
      total_amount: sumInvoicesInDefaultCurrency(invoices),
      total_paid_amount: sumInvoicesInDefaultCurrency(invoices, (inv) => inv.status === 'paid'),
      pending_amount: sumInvoicesInDefaultCurrency(invoices, (inv) => inv.status === 'submitted'),
      approved_amount: sumInvoicesInDefaultCurrency(invoices, (inv) => inv.status === 'approved'),
      currency: defaultCurrency
    };

    return stats;
  };

  return {
    defaultCurrency,
    currencyRates,
    loading,
    convertCurrency,
    sumInvoicesInDefaultCurrency,
    calculateStatisticsWithConversion,
    refreshCurrencySettings: fetchCurrencySettings
  };
};

export default useCurrency;
